import { AlgorithmStep, PathResult } from '@/pathfinding/core/PathResult';
import { BaseGridRenderer } from '@/grid/BaseGridRenderer';
import { CellState, Cell } from '@/grid/Cell';

export type AnimationState = 'idle' | 'playing' | 'paused' | 'finished';

export interface StepInfo {
  currentStep: number;
  openListSize: number;
  closedListSize: number;
  isComplete: boolean;
  path?: Cell[];
}

export class AnimationController {
  private generator: Generator<AlgorithmStep, PathResult, undefined> | null = null;
  private stepHistory: AlgorithmStep[] = [];
  private currentStepIndex: number = -1;
  private state: AnimationState = 'idle';
  private speed: number = 10;
  private animationId: number | null = null;
  private renderer: BaseGridRenderer;
  private finalResult: PathResult | null = null;

  private onStepChange?: (info: StepInfo) => void;
  private onStateChange?: (state: AnimationState) => void;
  private onPostRender?: () => void;

  constructor(renderer: BaseGridRenderer) {
    this.renderer = renderer;
  }

  loadGenerator(generator: Generator<AlgorithmStep, PathResult, undefined>): void {
    this.generator = generator;
    this.stepHistory = [];
    this.currentStepIndex = -1;
    this.state = 'idle';
    this.finalResult = null;
    this.onStateChange?.(this.state);
    this.notifyStepChange();
  }

  play(): void {
    if (this.state === 'playing') return;
    if (!this.generator && this.currentStepIndex >= this.stepHistory.length - 1 && this.finalResult) {
      return;
    }

    this.state = 'playing';
    this.onStateChange?.(this.state);
    this.animate();
  }

  pause(): void {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    this.state = 'paused';
    this.onStateChange?.(this.state);
  }

  stop(): void {
    this.pause();
    this.generator = null;
    this.stepHistory = [];
    this.currentStepIndex = -1;
    this.finalResult = null;
    this.state = 'idle';
    this.onStateChange?.(this.state);
    this.renderer.render();
    this.notifyStepChange();
  }

  stepForward(): boolean {
    if (this.currentStepIndex < this.stepHistory.length - 1) {
      this.currentStepIndex++;
      this.applyStep(this.stepHistory[this.currentStepIndex]);
      this.notifyStepChange();
      return true;
    }

    if (!this.generator) {
      return false;
    }

    const result = this.generator.next();

    if (result.done) {
      this.finalResult = result.value;
      this.generator = null;
      this.state = 'finished';
      this.onStateChange?.(this.state);

      if (this.finalResult.success) {
        for (const cell of this.finalResult.path) {
          if (!cell.isStart && !cell.isGoal) {
            cell.state = CellState.PATH;
          }
        }
        this.renderer.render();
        this.onPostRender?.();
      }

      this.notifyStepChange();
      return false;
    }

    const step = result.value;
    this.stepHistory.push(step);
    this.currentStepIndex = this.stepHistory.length - 1;
    this.applyStep(step);
    this.notifyStepChange();
    return true;
  }

  stepBackward(): boolean {
    if (this.currentStepIndex < 0) {
      return false;
    }

    this.currentStepIndex--;
    this.replayToStep(this.currentStepIndex);
    this.notifyStepChange();
    return true;
  }

  private animate(): void {
    if (this.state !== 'playing') return;

    let lastTime = performance.now();

    const frame = (currentTime: number) => {
      if (this.state !== 'playing') return;

      const delay = this.calculateDelay();
      const elapsed = currentTime - lastTime;

      if (elapsed >= delay) {
        const canContinue = this.stepForward();
        lastTime = currentTime;

        if (!canContinue) {
          this.state = 'finished';
          this.onStateChange?.(this.state);
          return;
        }
      }

      this.animationId = requestAnimationFrame(frame);
    };

    this.animationId = requestAnimationFrame(frame);
  }

  private applyStep(step: AlgorithmStep): void {
    switch (step.type) {
      case 'open':
        if (!step.cell.isStart && !step.cell.isGoal) {
          step.cell.state = CellState.OPEN;
        }
        break;
      case 'close':
        if (!step.cell.isStart && !step.cell.isGoal) {
          step.cell.state = CellState.CLOSED;
        }
        break;
      case 'path_found':
        break;
    }

    this.renderer.render();
    this.onPostRender?.();
  }

  private replayToStep(targetStep: number): void {
    for (let i = 0; i <= this.stepHistory.length - 1; i++) {
      const step = this.stepHistory[i];
      if (!step.cell.isStart && !step.cell.isGoal) {
        step.cell.state = CellState.UNVISITED;
      }
    }

    for (let i = 0; i <= targetStep && i < this.stepHistory.length; i++) {
      const step = this.stepHistory[i];
      switch (step.type) {
        case 'open':
          if (!step.cell.isStart && !step.cell.isGoal) {
            step.cell.state = CellState.OPEN;
          }
          break;
        case 'close':
          if (!step.cell.isStart && !step.cell.isGoal) {
            step.cell.state = CellState.CLOSED;
          }
          break;
      }
    }

    this.renderer.render();
    this.onPostRender?.();
  }

  private notifyStepChange(): void {
    const currentStep = this.stepHistory[this.currentStepIndex];
    const info: StepInfo = {
      currentStep: this.currentStepIndex + 1,
      openListSize: currentStep?.openListSize ?? 0,
      closedListSize: currentStep?.closedListSize ?? 0,
      isComplete: this.state === 'finished',
      path: this.finalResult?.path
    };
    this.onStepChange?.(info);
  }

  private calculateDelay(): number {
    return 1000 / this.speed;
  }

  setSpeed(speed: number): void {
    this.speed = Math.max(1, Math.min(100, speed));
  }

  setIterationsPerSecond(ips: number): void {
    this.speed = Math.max(1, Math.min(60, ips));
  }

  getSpeed(): number {
    return this.speed;
  }

  getState(): AnimationState {
    return this.state;
  }

  getCurrentStep(): number {
    return this.currentStepIndex + 1;
  }

  getTotalSteps(): number {
    return this.stepHistory.length;
  }

  canStepBackward(): boolean {
    return this.currentStepIndex >= 0;
  }

  canStepForward(): boolean {
    return this.generator !== null || this.currentStepIndex < this.stepHistory.length - 1;
  }

  isActive(): boolean {
    return this.generator !== null || this.stepHistory.length > 0;
  }

  getFinalResult(): PathResult | null {
    return this.finalResult;
  }

  onStepChangeCallback(callback: (info: StepInfo) => void): void {
    this.onStepChange = callback;
  }

  onStateChangeCallback(callback: (state: AnimationState) => void): void {
    this.onStateChange = callback;
  }

  onPostRenderCallback(callback: () => void): void {
    this.onPostRender = callback;
  }

  getStepHistory(): AlgorithmStep[] {
    return this.stepHistory;
  }

  destroy(): void {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    this.state = 'idle';
    this.generator = null;
    this.stepHistory = [];
    this.currentStepIndex = -1;
    this.finalResult = null;
  }
}
